import { isAbsolute } from "node:path";
import { buildAgentControllerRef } from "@wakeloop/agent-runtime";
import {
  createDefaultRuntimeAgentControllerSettings,
  getConfiguredDeviceRuntimeState,
  getConfiguredSkillsConfig,
  getRuntimeConfigReadHealth,
  type RuntimeAgentControllerConversationIdMappingSpec,
  type RuntimeAgentControllerState,
  type RuntimeAgentStreamingMode,
  type RuntimeAgentTransportMode,
  type RuntimeConfigReadHealth,
  type RuntimeSkillsConfig,
  type RuntimeSkillsCustomTarget,
  resolveRuntimeSkillsCustomTargetId,
  updateConfiguredDeviceRuntimeState,
  updateConfiguredSkillsConfig,
} from "../config/runtime-config";
import { notifyDaemonRouteCatalogChanged } from "../local-service/state/route-catalog-sync-notifier";
import {
  detectInstalledSkillsAgents,
  resolveSkillsAgentRegistry,
  type SkillsAgentRegistryEntry,
  type SkillsAgentTransportMode,
} from "../skills/agents";
import {
  type LocalControllerInstallChoice,
  type LocalControllerInstallStatus,
  resolveBuiltinControllerInstallResolution,
} from "./local-controller-install";
import {
  createAgentRegistration,
  normalizeAgentId,
  sortAgentRegistrations,
} from "./model";

const AGENTS_ERROR_CODE_PREFIX_RE = /^agents\.[a-z0-9_.-]+:/;

export type AgentTargetKind = "builtin" | "custom";
export type AgentLifecycleStatus = SkillsAgentRegistryEntry["lifecycleStatus"];

export type AgentTargetSelectableReason =
  | "not_detected"
  | "not_supported_yet"
  | "needs_repair"
  | "custom_coming_soon";

export type AgentTargetDescriptor = {
  agentId: string;
  displayName: string;
  targetKind: AgentTargetKind;
  detected: boolean;
  lifecycleStatus: AgentLifecycleStatus;
  runtimeSupported: boolean;
  transportModes: SkillsAgentTransportMode[];
  defaultTransportMode?: RuntimeAgentTransportMode;
  defaultStreamingMode?: RuntimeAgentStreamingMode;
  contextIdMappingSpec: RuntimeAgentControllerConversationIdMappingSpec;
  customDir?: string;
};

export type AgentCatalog = {
  detectedBuiltins: AgentTargetDescriptor[];
  builtinsById: Map<string, AgentTargetDescriptor>;
  customById: Map<string, AgentTargetDescriptor>;
};

export type AgentTargetState = AgentTargetDescriptor & {
  selectable: boolean;
  selectableReason: AgentTargetSelectableReason | null;
  installChoices: LocalControllerInstallChoice[];
  launchStatus: LocalControllerInstallStatus;
  launchStatusDetail: string | null;
  launchContract: RuntimeAgentControllerState["launchContract"] | null;
  registered: boolean;
  enabled: boolean;
  registration: RuntimeAgentControllerState | null;
};

export type AgentCustomTarget = {
  id: string;
  name: string;
  dir: string;
};

export type SetAgentRegistrationEnabledResult = {
  agentControllerRef: string;
  agentId: string;
  changed: boolean;
  displayName: string;
  enabled: boolean;
  status: "updated" | "unchanged";
};

export async function loadAgentCatalog(): Promise<AgentCatalog> {
  const skillsConfig = await getConfiguredSkillsConfig().catch((error) => {
    throw wrapConfigError(error, "agents.config_read_failed");
  });
  const builtins = resolveSkillsAgentRegistry(skillsConfig);
  const detectedIds = new Set(await detectInstalledSkillsAgents(builtins));
  const builtinsById = new Map<string, AgentTargetDescriptor>();
  for (const [agentId, entry] of Object.entries(builtins)) {
    builtinsById.set(agentId, {
      agentId,
      displayName: entry.displayName,
      targetKind: "builtin",
      detected: detectedIds.has(agentId),
      lifecycleStatus: entry.lifecycleStatus,
      runtimeSupported: entry.runtimeSupported,
      transportModes: entry.transportModes,
      ...(entry.defaultTransportMode
        ? { defaultTransportMode: entry.defaultTransportMode }
        : {}),
      ...(entry.defaultStreamingMode
        ? { defaultStreamingMode: entry.defaultStreamingMode }
        : {}),
      contextIdMappingSpec: resolveDefaultContextIdMappingSpec(agentId),
    });
  }

  const customById = new Map<string, AgentTargetDescriptor>();
  const customTargets = skillsConfig?.customTargets ?? [];
  for (const target of customTargets) {
    const resolvedId = resolveRuntimeSkillsCustomTargetId({
      dir: target.dir,
      ...(target.id ? { id: target.id } : {}),
    });
    customById.set(resolvedId, {
      agentId: resolvedId,
      displayName: target.name,
      targetKind: "custom",
      detected: false,
      lifecycleStatus: "active",
      runtimeSupported: false,
      transportModes: [],
      contextIdMappingSpec: resolveDefaultContextIdMappingSpec(resolvedId),
      customDir: target.dir,
    });
  }

  const detectedBuiltins = [...builtinsById.values()]
    .filter((entry) => {
      if (!entry.detected) {
        return false;
      }
      return resolveTargetSelectability(entry).selectable;
    })
    .sort((left, right) => left.agentId.localeCompare(right.agentId));

  return {
    detectedBuiltins,
    builtinsById,
    customById,
  };
}

export async function listAgentRegistrations(): Promise<
  RuntimeAgentControllerState[]
> {
  const [configured, skillsConfig] = await Promise.all([
    getConfiguredDeviceRuntimeState(),
    getConfiguredSkillsConfig(),
  ]).catch((error) => {
    throw wrapConfigError(error, "agents.config_read_failed");
  });
  const registrations = sortAgentRegistrations(configured?.controllers ?? []);
  if (registrations.length === 0) {
    return registrations;
  }
  const registry = resolveSkillsAgentRegistry(skillsConfig);
  const knownAgentIds = resolveKnownAgentIds(skillsConfig);
  const runtimeSupportedAgentIds = new Set(
    Object.entries(registry)
      .filter((entry) => entry[1].runtimeSupported)
      .map((entry) => entry[0])
  );
  const hasUnknownRegistrations = registrations.some(
    (registration) => !knownAgentIds.has(registration.agentId)
  );
  const hasUnsupportedRegistrations = registrations.some(
    (registration) => !runtimeSupportedAgentIds.has(registration.agentId)
  );
  const reconciliationTimestamp = new Date().toISOString();
  const reconciled = reconcileAgentRegistrationsForCurrentRegistry({
    knownAgentIds,
    registrations,
    registry,
    runtimeSupportedAgentIds,
    timestamp: reconciliationTimestamp,
  });
  if (
    !(
      hasUnknownRegistrations ||
      hasUnsupportedRegistrations ||
      reconciled.changed
    )
  ) {
    return registrations;
  }
  return updateAgentRegistrations(
    (current) =>
      reconcileAgentRegistrationsForCurrentRegistry({
        knownAgentIds,
        registrations: current,
        registry,
        runtimeSupportedAgentIds,
        timestamp: reconciliationTimestamp,
      }).registrations
  );
}

function reconcileAgentRegistrationsForCurrentRegistry(input: {
  knownAgentIds: Set<string>;
  registrations: RuntimeAgentControllerState[];
  registry: Record<string, SkillsAgentRegistryEntry>;
  runtimeSupportedAgentIds: Set<string>;
  timestamp: string;
}): {
  changed: boolean;
  registrations: RuntimeAgentControllerState[];
} {
  let changed = false;
  const registrations: RuntimeAgentControllerState[] = [];
  for (const registration of input.registrations) {
    if (
      !(
        input.knownAgentIds.has(registration.agentId) &&
        input.runtimeSupportedAgentIds.has(registration.agentId)
      )
    ) {
      changed = true;
      continue;
    }
    const reconciled = reconcileAgentRegistrationRuntimeDefaults({
      registration,
      registryEntry: input.registry[registration.agentId],
      timestamp: input.timestamp,
    });
    if (reconciled !== registration) {
      changed = true;
    }
    registrations.push(reconciled);
  }
  return {
    changed,
    registrations: sortAgentRegistrations(registrations),
  };
}

function reconcileAgentRegistrationRuntimeDefaults(input: {
  registration: RuntimeAgentControllerState;
  registryEntry?: SkillsAgentRegistryEntry;
  timestamp: string;
}): RuntimeAgentControllerState {
  const supportedTransportModes =
    input.registryEntry?.transportModes.map(
      (transportMode) => transportMode.mode
    ) ?? [];
  const defaultTransportMode =
    input.registryEntry?.defaultTransportMode ??
    supportedTransportModes[0] ??
    null;
  const transportModeSupported = input.registration.transportMode
    ? supportedTransportModes.includes(input.registration.transportMode)
    : false;
  if (!defaultTransportMode || transportModeSupported) {
    return input.registration;
  }
  return {
    ...input.registration,
    transportMode: defaultTransportMode,
    updatedAt: input.timestamp,
  };
}

export async function listAgentTargetStates(): Promise<AgentTargetState[]> {
  const [catalog, registrations] = await Promise.all([
    loadAgentCatalog(),
    listAgentRegistrations(),
  ]);
  const registrationById = new Map(
    registrations.map((registration) => [registration.agentId, registration])
  );
  const stateById = new Map<string, AgentTargetState>();

  // Built-in targets have higher priority than custom targets when ids collide.
  for (const target of catalog.builtinsById.values()) {
    const registration = registrationById.get(target.agentId) ?? null;
    stateById.set(
      target.agentId,
      await toAgentTargetState(target, registration)
    );
  }

  for (const target of catalog.customById.values()) {
    if (stateById.has(target.agentId)) {
      continue;
    }
    const registration = registrationById.get(target.agentId) ?? null;
    stateById.set(
      target.agentId,
      await toAgentTargetState(target, registration)
    );
  }

  return [...stateById.values()].sort((left, right) =>
    left.agentId.localeCompare(right.agentId)
  );
}

export async function resolveAgentTargetState(
  rawAgentId: string,
  states?: AgentTargetState[]
): Promise<AgentTargetState | null> {
  const agentId = normalizeAgentId(rawAgentId);
  if (!agentId) {
    return null;
  }
  if (states) {
    return states.find((state) => state.agentId === agentId) ?? null;
  }

  const [catalog, registrations] = await Promise.all([
    loadAgentCatalog(),
    listAgentRegistrations(),
  ]);
  const registrationById = new Map(
    registrations.map((registration) => [registration.agentId, registration])
  );

  const builtIn = catalog.builtinsById.get(agentId);
  if (builtIn) {
    return await toAgentTargetState(
      builtIn,
      registrationById.get(builtIn.agentId) ?? null
    );
  }

  const custom = catalog.customById.get(agentId);
  if (custom) {
    return await toAgentTargetState(
      custom,
      registrationById.get(custom.agentId) ?? null
    );
  }

  return null;
}

export async function updateAgentRegistrations(
  updater: (
    current: RuntimeAgentControllerState[]
  ) => RuntimeAgentControllerState[]
): Promise<RuntimeAgentControllerState[]> {
  let nextRegistrations: RuntimeAgentControllerState[] = [];
  try {
    await updateConfiguredDeviceRuntimeState((currentConfig) => {
      const current = sortAgentRegistrations(currentConfig?.controllers ?? []);
      nextRegistrations = sortAgentRegistrations(updater(current));
      return { controllers: nextRegistrations };
    });
  } catch (error) {
    throw wrapConfigError(error, "agents.config_write_failed");
  }
  await notifyDaemonRouteCatalogChanged({
    reason: "device_runtime_state_updated",
  }).catch(() => undefined);
  return nextRegistrations;
}

export async function listAgentCustomTargets(): Promise<AgentCustomTarget[]> {
  const skillsConfig = await getConfiguredSkillsConfig().catch((error) => {
    throw wrapConfigError(error, "agents.config_read_failed");
  });
  return normalizeCustomTargets(skillsConfig?.customTargets);
}

export async function addAgentCustomTarget(input: {
  name: string;
  dir: string;
  id?: string;
}): Promise<AgentCustomTarget> {
  const name = normalizeAgentCustomTargetField(input.name);
  const dir = normalizeAgentCustomTargetField(input.dir);
  if (!(name && dir)) {
    throw new Error(
      "agents.custom.invalid_argument: --name and --dir are required for add"
    );
  }
  if (!isAbsolute(dir)) {
    throw new Error(
      "agents.custom.invalid_argument: --dir must be an absolute path"
    );
  }
  const requestedId = normalizeOptionalAgentCustomTargetId(input.id);
  const targetId = resolveRuntimeSkillsCustomTargetId({
    dir,
    ...(requestedId ? { id: requestedId } : {}),
  });

  let created: AgentCustomTarget | null = null;
  try {
    await updateConfiguredSkillsConfig((currentSkillsConfig) => {
      const normalizedTargets = normalizeCustomTargets(
        currentSkillsConfig?.customTargets
      );
      if (normalizedTargets.some((target) => target.id === targetId)) {
        throw new Error(
          `agents.custom.conflict: id already exists: ${targetId}`
        );
      }
      if (normalizedTargets.some((target) => target.dir === dir)) {
        throw new Error(`agents.custom.conflict: dir already exists: ${dir}`);
      }
      created = {
        id: targetId,
        name,
        dir,
      };
      return buildSkillsConfigWithCustomTargets({
        currentSkillsConfig,
        targets: sortCustomTargets([...normalizedTargets, created]),
      });
    });
  } catch (error) {
    throw wrapConfigError(error, "agents.config_write_failed");
  }

  if (!created) {
    throw new Error("agents.config_write_failed: custom target add failed");
  }
  await notifyDaemonRouteCatalogChanged({
    reason: "custom_target_added",
  }).catch(() => undefined);
  return created;
}

export async function updateAgentCustomTarget(input: {
  id: string;
  name?: string;
  dir?: string;
}): Promise<{
  target: AgentCustomTarget;
  updatedFields: string[];
}> {
  const targetId = normalizeAgentId(input.id);
  if (!targetId) {
    throw new Error("agents.custom.invalid_argument: --id is required");
  }
  const normalizedName = normalizeOptionalAgentCustomTargetField(input.name);
  const normalizedDir = normalizeOptionalAgentCustomTargetField(input.dir);
  if (normalizedName === undefined && normalizedDir === undefined) {
    throw new Error(
      "agents.custom.invalid_argument: provide at least one option (--name/--dir)"
    );
  }
  if (normalizedDir && !isAbsolute(normalizedDir)) {
    throw new Error(
      "agents.custom.invalid_argument: --dir must be an absolute path"
    );
  }

  let nextTarget: AgentCustomTarget | null = null;
  const updatedFields: string[] = [];
  try {
    await updateConfiguredSkillsConfig((currentSkillsConfig) => {
      const normalizedTargets = normalizeCustomTargets(
        currentSkillsConfig?.customTargets
      );
      const index = normalizedTargets.findIndex(
        (target) => target.id === targetId
      );
      if (index < 0) {
        throw new Error(
          `agents.custom.not_found: target does not exist: ${targetId}`
        );
      }
      const selectedTarget = normalizedTargets[index];
      if (!selectedTarget) {
        throw new Error(
          "agents.config_write_failed: custom target index missing"
        );
      }

      const updatedTarget: AgentCustomTarget = {
        ...selectedTarget,
      };
      if (
        normalizedName !== undefined &&
        normalizedName.length > 0 &&
        normalizedName !== selectedTarget.name
      ) {
        updatedTarget.name = normalizedName;
        updatedFields.push("name");
      }

      if (
        normalizedDir !== undefined &&
        normalizedDir.length > 0 &&
        normalizedDir !== selectedTarget.dir
      ) {
        const dirConflict = normalizedTargets.some(
          (target) => target.id !== targetId && target.dir === normalizedDir
        );
        if (dirConflict) {
          throw new Error(
            `agents.custom.conflict: dir already exists: ${normalizedDir}`
          );
        }
        updatedTarget.dir = normalizedDir;
        updatedFields.push("dir");
      }

      const nextTargets = [...normalizedTargets];
      nextTargets[index] = updatedTarget;
      nextTarget = updatedTarget;
      return buildSkillsConfigWithCustomTargets({
        currentSkillsConfig,
        targets: sortCustomTargets(nextTargets),
      });
    });
  } catch (error) {
    throw wrapConfigError(error, "agents.config_write_failed");
  }

  if (!nextTarget) {
    throw new Error("agents.config_write_failed: custom target update failed");
  }
  await notifyDaemonRouteCatalogChanged({
    reason: "custom_target_updated",
  }).catch(() => undefined);
  return {
    target: nextTarget,
    updatedFields,
  };
}

export async function removeAgentCustomTarget(input: {
  id: string;
}): Promise<AgentCustomTarget> {
  const targetId = normalizeAgentId(input.id);
  if (!targetId) {
    throw new Error("agents.custom.invalid_argument: --id is required");
  }

  let removedTarget: AgentCustomTarget | null = null;
  try {
    await updateConfiguredSkillsConfig((currentSkillsConfig) => {
      const normalizedTargets = normalizeCustomTargets(
        currentSkillsConfig?.customTargets
      );
      const index = normalizedTargets.findIndex(
        (target) => target.id === targetId
      );
      if (index < 0) {
        throw new Error(
          `agents.custom.not_found: target does not exist: ${targetId}`
        );
      }
      const target = normalizedTargets[index];
      if (!target) {
        throw new Error(
          "agents.config_write_failed: custom target index missing"
        );
      }
      removedTarget = target;
      const nextTargets = normalizedTargets.filter(
        (item) => item.id !== targetId
      );
      return buildSkillsConfigWithCustomTargets({
        currentSkillsConfig,
        targets: sortCustomTargets(nextTargets),
      });
    });
  } catch (error) {
    throw wrapConfigError(error, "agents.config_write_failed");
  }

  if (!removedTarget) {
    throw new Error("agents.config_write_failed: custom target remove failed");
  }
  await notifyDaemonRouteCatalogChanged({
    reason: "custom_target_removed",
  }).catch(() => undefined);
  return removedTarget;
}

export async function readAgentRuntimeConfigHealth(): Promise<RuntimeConfigReadHealth> {
  try {
    return await getRuntimeConfigReadHealth();
  } catch (error) {
    throw wrapConfigError(error, "agents.config_read_failed");
  }
}

export function resolveCatalogTarget(
  catalog: AgentCatalog,
  rawAgentId: string
): AgentTargetDescriptor | null {
  const agentId = normalizeAgentId(rawAgentId);
  if (!agentId) {
    return null;
  }
  const builtin = catalog.builtinsById.get(agentId);
  if (builtin) {
    return builtin;
  }
  const custom = catalog.customById.get(agentId);
  if (custom) {
    return custom;
  }
  return null;
}

export function createRegistrationFromCatalogTarget(
  target: AgentTargetState
): RuntimeAgentControllerState {
  return createAgentRegistration({
    agentId: target.agentId,
    displayName: target.displayName,
    ...(target.launchContract ? { launchContract: target.launchContract } : {}),
    ...(target.defaultStreamingMode
      ? { streamingMode: target.defaultStreamingMode }
      : {}),
    ...(target.defaultTransportMode
      ? { transportMode: target.defaultTransportMode }
      : {}),
  });
}

export async function setAgentRegistrationEnabled(input: {
  agentId: string;
  enabled: boolean;
}): Promise<SetAgentRegistrationEnabledResult> {
  const agentId = normalizeAgentId(input.agentId);
  if (!agentId) {
    throw new Error(
      "agents.invalid_agent_id: provide a non-empty local agent id"
    );
  }

  const state = await resolveAgentTargetState(agentId);
  if (state?.targetKind !== "builtin") {
    throw new Error(`agents.invalid_agent_id: unknown local agent: ${agentId}`);
  }
  if (!state.registration) {
    throw new Error(
      `agents.not_registered: agent is not registered: ${agentId}`
    );
  }
  if (input.enabled && !state.selectable) {
    throw new Error(
      `agents.invalid_agent_id: ${resolveAgentEnableBlockedReason(state)}`
    );
  }

  const agentControllerRef = buildAgentControllerRef({
    kind: "builtin",
    id: state.agentId,
  });
  let result: SetAgentRegistrationEnabledResult | null = null;
  await updateAgentRegistrations((current) => {
    const index = current.findIndex((item) => item.agentId === state.agentId);
    if (index < 0) {
      throw new Error(
        `agents.not_registered: agent is not registered: ${state.agentId}`
      );
    }
    const existing = current[index];
    if (!existing) {
      throw new Error("agents.config_write_failed: registration index missing");
    }

    if (existing.enabled === input.enabled) {
      result = {
        agentControllerRef,
        agentId: state.agentId,
        changed: false,
        displayName: existing.displayName,
        enabled: existing.enabled,
        status: "unchanged",
      };
      return current;
    }

    const next = [...current];
    next[index] = {
      ...existing,
      enabled: input.enabled,
      updatedAt: new Date().toISOString(),
    };
    result = {
      agentControllerRef,
      agentId: state.agentId,
      changed: true,
      displayName: existing.displayName,
      enabled: input.enabled,
      status: "updated",
    };
    return sortAgentRegistrations(next);
  });

  if (!result) {
    throw new Error(
      "agents.config_write_failed: enabled update result missing"
    );
  }
  return result;
}

function resolveAgentEnableBlockedReason(state: AgentTargetState): string {
  if (!state.detected) {
    return `${state.displayName} is not installed on this computer`;
  }
  if (
    !state.runtimeSupported ||
    state.selectableReason === "not_supported_yet"
  ) {
    return `${state.displayName} is not supported by the local WakeLoop Service yet`;
  }
  if (state.launchStatus === "needs_choice") {
    return (
      state.launchStatusDetail ??
      `WakeLoop needs a local install choice before ${state.displayName} can be turned on`
    );
  }
  if (state.launchStatus === "needs_repair") {
    return (
      state.launchStatusDetail ??
      `WakeLoop needs to repair how ${state.displayName} starts on this computer`
    );
  }
  if (state.selectableReason) {
    return `${state.displayName} is not selectable: ${state.selectableReason}`;
  }
  return `${state.displayName} cannot be turned on from WakeLoop Web`;
}

async function toAgentTargetState(
  target: AgentTargetDescriptor,
  registration: RuntimeAgentControllerState | null
): Promise<AgentTargetState> {
  const launchResolution =
    target.targetKind === "builtin"
      ? await resolveBuiltinControllerInstallResolution({
          agentId: target.agentId,
          detected: target.detected,
          displayName: target.displayName,
          persistedLaunchContract: registration?.launchContract ?? null,
          runtimeSupported: target.runtimeSupported,
        })
      : {
          detail: null,
          installChoices: [],
          launchContract: null,
          status: "not_available" as const,
        };
  const normalizedLaunchResolution =
    normalizeBuiltinControllerInstallResolutionForRuntime({
      displayName: target.displayName,
      launchResolution,
      registration,
      targetKind: target.targetKind,
    });
  const selectability = resolveTargetSelectability({
    detected: target.detected,
    launchStatus: normalizedLaunchResolution.status,
    runtimeSupported: target.runtimeSupported,
    targetKind: target.targetKind,
  });
  return {
    ...target,
    selectable: selectability.selectable,
    selectableReason: selectability.reason,
    installChoices: normalizedLaunchResolution.installChoices,
    launchStatus: normalizedLaunchResolution.status,
    launchStatusDetail: normalizedLaunchResolution.detail,
    launchContract: normalizedLaunchResolution.launchContract,
    registered: Boolean(registration),
    enabled: registration?.enabled ?? false,
    contextIdMappingSpec:
      registration?.contextIdMappingSpec ?? target.contextIdMappingSpec,
    registration,
  };
}

function normalizeBuiltinControllerInstallResolutionForRuntime(input: {
  displayName: string;
  launchResolution: Awaited<
    ReturnType<typeof resolveBuiltinControllerInstallResolution>
  >;
  registration: RuntimeAgentControllerState | null;
  targetKind: AgentTargetKind;
}) {
  if (
    !(
      input.targetKind === "builtin" &&
      input.registration?.enabled === true &&
      !input.registration.launchContract
    )
  ) {
    return input.launchResolution;
  }

  return {
    detail: `${input.displayName} is connected, but WakeLoop needs to repair how it starts on this computer.`,
    installChoices: [],
    launchContract: null,
    status: "needs_repair" as const,
  };
}

export async function resolveAgentContextBinding(input: {
  agentId: string;
  states?: AgentTargetState[];
  env?: Record<string, string | undefined>;
}): Promise<{
  agentControllerConversationId: string | null;
  sourceField: string;
  mappingVersion: number;
} | null> {
  const state = await resolveAgentTargetState(input.agentId, input.states);
  if (!state) {
    return null;
  }

  const sourceField = state.contextIdMappingSpec.sourceField;
  const env = input.env ?? process.env;
  const rawValue = env[sourceField];
  const agentControllerConversationId =
    typeof rawValue === "string" && rawValue.trim().length > 0
      ? rawValue.trim()
      : null;

  return {
    agentControllerConversationId,
    sourceField,
    mappingVersion: state.contextIdMappingSpec.mappingVersion,
  };
}

function wrapConfigError(error: unknown, fallbackCode: string): Error {
  if (
    error instanceof Error &&
    AGENTS_ERROR_CODE_PREFIX_RE.test(error.message)
  ) {
    return error;
  }
  const reason =
    error instanceof Error && error.message.trim().length > 0
      ? error.message.trim()
      : "unexpected runtime config failure";
  return new Error(`${fallbackCode}: ${reason}`);
}

function resolveDefaultContextIdMappingSpec(
  agentId: string
): RuntimeAgentControllerConversationIdMappingSpec {
  return createDefaultRuntimeAgentControllerSettings(agentId)
    .contextIdMappingSpec;
}

function resolveKnownAgentIds(
  skillsConfig: RuntimeSkillsConfig | null
): Set<string> {
  const builtinRegistry = resolveSkillsAgentRegistry(skillsConfig);
  const knownAgentIds = new Set<string>(Object.keys(builtinRegistry));
  for (const target of normalizeCustomTargets(skillsConfig?.customTargets)) {
    knownAgentIds.add(target.id);
  }
  return knownAgentIds;
}

function resolveTargetSelectability(input: {
  targetKind: AgentTargetKind;
  detected: boolean;
  launchStatus?: LocalControllerInstallStatus;
  runtimeSupported: boolean;
}): { selectable: boolean; reason: AgentTargetSelectableReason | null } {
  if (input.targetKind === "custom") {
    return {
      selectable: false,
      reason: "custom_coming_soon",
    };
  }
  if (!input.detected) {
    return {
      selectable: false,
      reason: "not_detected",
    };
  }
  if (!input.runtimeSupported) {
    return {
      selectable: false,
      reason: "not_supported_yet",
    };
  }
  return {
    selectable: true,
    reason: null,
  };
}

function normalizeCustomTargets(
  targets: RuntimeSkillsCustomTarget[] | undefined
): AgentCustomTarget[] {
  const byId = new Map<string, AgentCustomTarget>();
  for (const target of targets ?? []) {
    const name = normalizeAgentCustomTargetField(target.name);
    const dir = normalizeAgentCustomTargetField(target.dir);
    if (!(name && dir)) {
      continue;
    }
    const id = resolveRuntimeSkillsCustomTargetId({
      dir,
      ...(target.id ? { id: target.id } : {}),
    });
    byId.set(id, {
      id,
      name,
      dir,
    });
  }
  return sortCustomTargets([...byId.values()]);
}

function sortCustomTargets(targets: AgentCustomTarget[]): AgentCustomTarget[] {
  return [...targets].sort((left, right) => left.id.localeCompare(right.id));
}

function normalizeAgentCustomTargetField(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeOptionalAgentCustomTargetId(
  value: unknown
): string | undefined {
  const normalized = normalizeAgentCustomTargetField(value);
  return normalized.length > 0 ? normalized : undefined;
}

function normalizeOptionalAgentCustomTargetField(
  value: unknown
): string | undefined {
  const normalized = normalizeAgentCustomTargetField(value);
  return normalized.length > 0 ? normalized : undefined;
}

function buildSkillsConfigWithCustomTargets(input: {
  currentSkillsConfig: RuntimeSkillsConfig | null;
  targets: AgentCustomTarget[];
}): RuntimeSkillsConfig | null {
  const nextTargets: RuntimeSkillsCustomTarget[] = input.targets.map(
    (target) => ({
      id: target.id,
      name: target.name,
      dir: target.dir,
    })
  );
  const current = input.currentSkillsConfig;
  if (!current && nextTargets.length === 0) {
    return null;
  }

  return {
    ...(current?.storeDir ? { storeDir: current.storeDir } : {}),
    ...(current?.agents ? { agents: current.agents } : {}),
    ...(nextTargets.length > 0 ? { customTargets: nextTargets } : {}),
    ...(current?.installs ? { installs: current.installs } : {}),
  };
}
